import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Container,
  Button,
  Form,
  Card,
  Alert,
  Tab,
  Tabs,
  Spinner,
  ListGroup,
  Badge,
} from "react-bootstrap";
import SidebarBoarding from "../../components/SidebarDashboard";
import CustomNavbar from "../../components/CustomNavbar";
import DNCManagement from "./DNCManagement";
import CoursesManagement from "./CoursesManagement";
import {
  getEmployeeForm,
  createFormResponse,
} from "../../redux/capacitacionDesarrollo/DNC/actions";

function TrainingNeedsManagement() {
  const dispatch = useDispatch();
  // Estados
  const [show, setShow] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  //Store
  const userRoles = useSelector((store) => store.auth.userRoles);
  const { username } = useSelector((store) => store.auth);
  const { loading, saveLoading, employeeForm, error } = useSelector(
    (store) => store.DNC
  );

  // Verifica roles
  const isAdmin = userRoles.includes("administrador");
  const isTrainingResponsible = userRoles.includes("Responsable_Capacitacion");
  const isBoss = userRoles.includes("jefe");

  const [activeTab, setActiveTab] = useState(() => {
    if (isAdmin || isTrainingResponsible) return "dnc";
    if (isBoss) return "needs";
    return "needs";
  });

  useEffect(() => {
  if (isAdmin || isTrainingResponsible) {
    setActiveTab("dnc");
  } else if (isBoss) {
    setActiveTab("needs");
  }
}, [isAdmin, isTrainingResponsible, isBoss]);

  const showNeedsTab = isBoss;
  const showDNCTab = isAdmin || isTrainingResponsible;
  const showCoursesTab = isAdmin || isTrainingResponsible;

  const onShowSidebar = () => setShow(!show);

  useEffect(() => {
    if (username) {
      dispatch(getEmployeeForm({ codigo_empleado: username }));
    }
  }, [dispatch, username]);

  useEffect(() => {
    if (employeeForm?.preguntas_activas) {
      const initialResponses = {};
      employeeForm.preguntas_activas.forEach((pregunta) => {
        if (pregunta.tipo === "seleccion_multiple") {
          initialResponses[pregunta.id] = [];
        } else {
          initialResponses[pregunta.id] = "";
        }
      });
      setResponses(initialResponses);
    }
  }, [employeeForm]);

  const handleInputChange = (e, questionId) => {
    const { value } = e.target;
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    if (errors[questionId]) {
      setErrors((prev) => ({ ...prev, [questionId]: "" }));
    }
  };

  const handleCheckboxChange = (e, questionId, optionId) => {
    const isChecked = e.target.checked;
    setResponses((prev) => {
      const currentValues = prev[questionId] || [];
      return {
        ...prev,
        [questionId]: isChecked
          ? [...currentValues, optionId]
          : currentValues.filter((id) => id !== optionId),
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    employeeForm.preguntas_activas.forEach((pregunta) => {
      if (pregunta.obligatoria) {
        if (
          pregunta.tipo === "texto" &&
          (!responses[pregunta.id] || responses[pregunta.id].trim() === "")
        ) {
          newErrors[pregunta.id] = "Esta pregunta es obligatoria";
          isValid = false;
        } else if (
          (pregunta.tipo === "seleccion_unica" ||
            pregunta.tipo === "seleccion_multiple") &&
          (!responses[pregunta.id] || responses[pregunta.id].length === 0)
        ) {
          newErrors[pregunta.id] = "Debe seleccionar al menos una opción";
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const formatResponsesForSubmission = () => {
    const formattedResponses = [];

    Object.keys(responses).forEach((questionId) => {
      const pregunta = employeeForm.preguntas_activas.find(
        (p) => p.id === parseInt(questionId)
      );

      if (pregunta.tipo === "texto") {
        if (responses[questionId] && responses[questionId].trim() !== "") {
          formattedResponses.push({
            pregunta_id: parseInt(questionId),
            valor_texto: responses[questionId],
          });
        }
      } else if (pregunta.tipo === "seleccion_unica") {
        if (responses[questionId]) {
          formattedResponses.push({
            pregunta_id: parseInt(questionId),
            valor_opcion: responses[questionId],
          });
        }
      } else if (pregunta.tipo === "seleccion_multiple") {
        responses[questionId]?.forEach((optionId) => {
          formattedResponses.push({
            pregunta_id: parseInt(questionId),
            valor_opcion: optionId,
          });
        });
      }
    });

    return formattedResponses;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      codigo_empleado: username,
      respuestas: formatResponsesForSubmission(),
    };

    dispatch(createFormResponse(payload)).then(() => {
      setSubmitted(true);
      setShowForm(false);
      dispatch(getEmployeeForm({ codigo_empleado: username }));
    });
  };

  const renderQuestion = (pregunta) => {
    switch (pregunta.tipo) {
      case "texto":
        return (
          <Form.Group className="mb-4" key={pregunta.id}>
            <Form.Label>
              {pregunta.texto}
              {pregunta.obligatoria && (
                <Badge pill bg="danger" className="ms-2">
                  Obligatoria
                </Badge>
              )}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={responses[pregunta.id] || ""}
              onChange={(e) => handleInputChange(e, pregunta.id)}
              isInvalid={!!errors[pregunta.id]}
            />
            <Form.Control.Feedback type="invalid">
              {errors[pregunta.id]}
            </Form.Control.Feedback>
          </Form.Group>
        );

      case "seleccion_unica":
        return (
          <Form.Group className="mb-4" key={pregunta.id}>
            <Form.Label>
              {pregunta.texto}
              {pregunta.obligatoria && (
                <Badge pill bg="danger" className="ms-2">
                  Obligatoria
                </Badge>
              )}
            </Form.Label>
            <ListGroup>
              {pregunta.opciones.map((opcion) => (
                <ListGroup.Item key={opcion.id}>
                  <Form.Check
                    type="radio"
                    name={`pregunta-${pregunta.id}`}
                    id={`opcion-${opcion.id}`}
                    label={opcion.texto}
                    checked={responses[pregunta.id] === opcion.id}
                    onChange={() => {
                      setResponses((prev) => ({
                        ...prev,
                        [pregunta.id]: opcion.id,
                      }));
                      if (errors[pregunta.id]) {
                        setErrors((prev) => ({ ...prev, [pregunta.id]: "" }));
                      }
                    }}
                  />
                </ListGroup.Item>
              ))}
            </ListGroup>
            {errors[pregunta.id] && (
              <Form.Text className="text-danger">
                {errors[pregunta.id]}
              </Form.Text>
            )}
          </Form.Group>
        );

      case "seleccion_multiple":
        return (
          <Form.Group className="mb-4" key={pregunta.id}>
            <Form.Label>
              {pregunta.texto}
              {pregunta.obligatoria && (
                <Badge pill bg="danger" className="ms-2">
                  Obligatoria
                </Badge>
              )}
            </Form.Label>
            <ListGroup>
              {pregunta.opciones.map((opcion) => (
                <ListGroup.Item key={opcion.id}>
                  <Form.Check
                    type="checkbox"
                    id={`opcion-${opcion.id}`}
                    label={opcion.texto}
                    checked={
                      responses[pregunta.id] &&
                      responses[pregunta.id].includes(opcion.id)
                    }
                    onChange={(e) => {
                      handleCheckboxChange(e, pregunta.id, opcion.id);
                      if (
                        errors[pregunta.id] &&
                        responses[pregunta.id]?.length > 0
                      ) {
                        setErrors((prev) => ({ ...prev, [pregunta.id]: "" }));
                      }
                    }}
                  />
                </ListGroup.Item>
              ))}
            </ListGroup>
            {errors[pregunta.id] && (
              <Form.Text className="text-danger">
                {errors[pregunta.id]}
              </Form.Text>
            )}
          </Form.Group>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SidebarBoarding show={show} onShowSidebar={onShowSidebar} />
      <CustomNavbar type={"dashboard"} onShowSidebar={onShowSidebar} />
      <div className="custom-wrapper">
        <Container fluid>
          <div className="my-1 pl-md-4 pr-md-3 pl-2 pr-1 d-flex flex-wrap justify-content-between align-items-center">
            <h5 className="text-black-50 m-0 text-uppercase">
              Gestión de Necesidades de Capacitación
            </h5>
            {activeTab === "needs" && isBoss && !employeeForm?.ya_respondio && (
              <Button
                variant="light"
                size="sm"
                className="rounded-pill"
                onClick={() => setShowForm(true)}
              >
                <i className="material-icons">add</i> Responder Formulario
              </Button>
            )}
          </div>

          {submitted && (
            <Alert variant="success" className="mt-4 mb-1 mx-3">
              ¡Formulario enviado correctamente!
            </Alert>
          )}

          {error && (
            <Alert variant="danger" className="mt-4 mb-1 mx-3">
              {error}
            </Alert>
          )}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
            id="training-management-tabs"
          >
            {showNeedsTab && (
              <Tab eventKey="needs" title="Necesidades" disabled={!isBoss}>
                {loading ? (
                  <div className="d-flex justify-content-center my-5">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : employeeForm?.ya_respondio ? (
                  <Alert variant="success" className="my-4">
                    Ya has respondido el formulario de necesidades de
                    capacitación para este año.
                  </Alert>
                ) : showForm ? (
                  <Card className="border-0">
                    <Card.Body className="px-1 px-sm-2">
                      <h5 className="text-black-50 mb-4">
                        Formulario de Detección de Necesidades de Capacitación
                      </h5>
                      <Form onSubmit={handleSubmit}>
                        {employeeForm?.preguntas_activas?.map((pregunta) =>
                          renderQuestion(pregunta)
                        )}

                        <div className="d-flex justify-content-end gap-2 mt-4">
                          <Button
                            variant="outline-secondary"
                            onClick={() => setShowForm(false)}
                            className="rounded-pill"
                          >
                            Cancelar
                          </Button>
                          <Button
                            variant="primary"
                            type="submit"
                            className="rounded-pill"
                            disabled={saveLoading}
                          >
                            {saveLoading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Enviando...
                              </>
                            ) : (
                              "Enviar Respuestas"
                            )}
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                ) : (
                  <Alert variant="info" className="my-4">
                    Presione el botón "Responder Formulario" para completar el
                    formulario de detección de necesidades de capacitación.
                  </Alert>
                )}
              </Tab>
            )}

            {showDNCTab && (
              <Tab eventKey="dnc" title="DNC">
                <DNCManagement />
              </Tab>
            )}

            {showCoursesTab && (
              <Tab eventKey="courses" title="Cursos">
                <CoursesManagement />
              </Tab>
            )}
          </Tabs>
        </Container>
      </div>
    </>
  );
}

export default TrainingNeedsManagement;
